import './Publications.css';
import '../Footer/Footer'
import React, { useEffect, useMemo, useState } from "react";
import BannerModal from './PublicationModal';
import { getAllDataWithFlag } from "../../../services/CommonService";
import { imgURL } from '../../Config';
import Swal from 'sweetalert2';

const normalizeArrayValue = (value) => {
  if (Array.isArray(value)) {
    return value
      .map((item) => String(item).trim())
      .filter(Boolean);
  }

  if (value === null || value === undefined || value === '') {
    return [];
  }

  return String(value)
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
};

const normalizeString = (value) => String(value || '').trim().toLowerCase();

function Publications({ projectId }) {
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [publications, setPublication] = useState([]);
  const [isGridView, setIsGridView] = useState(true);
  const [selectedCard, setSelectedCard] = useState(null);
  const [CountryData, setCountryData] = useState([]);
  const [contentData, setTypeofContent] = useState([]);
  const [StateData, setStateData] = useState([]);
  const [DistrictData, setDistrictData] = useState([]);
  const [typologyData, setTypologyData] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: '', direction: 'asc' });
  const [districtList, setDistrictList] = useState([]);
  const [lookup, setLookup] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);

  const [searchFilters, setSearchFilters] = useState({
    year: '',
    type: '',
    typology: '',
    district: '',
    state: '',
  });

  useEffect(() => {
    const lookupData = JSON.parse(localStorage.getItem('lookup') || '[]');
    const stateMaster = JSON.parse(localStorage.getItem('StateMaster') || '{}');

    setLookup(lookupData);
    setCountryData(stateMaster.country || []);
    setStateData(stateMaster.state || []);
    setDistrictData(stateMaster.district || []);
    setTypeofContent(lookupData.filter((item) => String(item.Flag) === '4'));
    setTypologyData(lookupData.filter((item) => String(item.Flag) === '3'));
  }, []);

  useEffect(() => {
    if (CountryData.length && StateData.length && DistrictData.length) {
      getAllaData();
    }
  }, [CountryData, StateData, DistrictData, contentData, typologyData, projectId]);

  useEffect(() => {
    if (!searchFilters.state) {
      setDistrictList([]);
      return;
    }

    const filteredDistricts = DistrictData.filter(
      (district) => String(district.id_mststate) === String(searchFilters.state)
    );
    setDistrictList(filteredDistricts);
  }, [DistrictData, searchFilters.state]);

  const getAllaData = async () => {
    try {
      setLoading(true);

      const [pubRes, projectRes] = await Promise.all([
        getAllDataWithFlag(4),
        getAllDataWithFlag(5)
      ]);

      if (pubRes.status !== 200 || projectRes.status !== 200) {
        throw new Error("API Error");
      }

      const publicationData = pubRes.data.publication || [];
      const projectData = projectRes.data.project || [];

      const mergedArray = publicationData
        .filter((item) => item?.is_deleted !== 1 && item?.is_deleted !== '1')
        .filter((item) => !projectId || String(item?.project_id) === String(projectId))
        .map((item) => {
          const project = projectData.find((record) => String(record?.id) === String(item?.project_id));
          const stateIds = normalizeArrayValue(item?.state).length
            ? normalizeArrayValue(item?.state)
            : normalizeArrayValue(project?.state);
          const districtIds = normalizeArrayValue(item?.district).length
            ? normalizeArrayValue(item?.district)
            : normalizeArrayValue(project?.district);

          const matchedStates = StateData.filter((state) =>
            stateIds.includes(String(state.id_mststate))
          );
          const matchedDistricts = DistrictData.filter((district) =>
            districtIds.includes(String(district.id_mstdistrict))
          );
          const matchedCountry = CountryData.find((country) => String(country.id) === String(item?.country));
          const matchedTypology = typologyData.find((typo) => String(typo.LookupCode) === String(item?.typology));
          const matchedType = contentData.find((type) => String(type.LookupCode) === String(item?.type));
          const matchedMode = lookup.find(
            (entry) => String(entry.Flag) === '7' && String(entry.LookupCode) === String(item?.link)
          );

          return {
            ...item,
            stateIds,
            districtIds,
            countryName: matchedCountry?.CountryName || null,
            stateName: matchedStates
              .map((state) => state.statename?.trim())
              .filter(Boolean)
              .join(', ') || null,
            districtName: matchedDistricts
              .map((district) => district.districtname?.trim())
              .filter(Boolean)
              .join(', ') || null,
            projectName: project?.title || null,
            typologyName: matchedTypology?.LookupValue || null,
            typeName: matchedType?.LookupValue || null,
            linkName: matchedMode?.LookupValue || null
          };
        });

      setPublication(mergedArray);
    } catch (error) {
      Swal.fire({
        title: "Error",
        text: error?.response?.data?.message || error?.message,
        icon: "error",
        confirmButtonText: "OK",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setSearchFilters((prev) => ({
      ...prev,
      [name]: value,
      ...(name === 'state' && { district: '' })
    }));
  };

  const filteredPublications = useMemo(() => (
    publications.filter((pub) => {
      const yearMatches = !searchFilters.year || String(pub?.year || '').trim() === String(searchFilters.year);
      const typeMatches = !searchFilters.type || String(pub?.type || '').trim() === String(searchFilters.type);
      const typologyMatches = !searchFilters.typology || String(pub?.typology || '').trim() === String(searchFilters.typology);
      const stateMatches = !searchFilters.state || pub.stateIds?.includes(String(searchFilters.state));
      const districtMatches = !searchFilters.district || pub.districtIds?.includes(String(searchFilters.district));

      return yearMatches && typeMatches && typologyMatches && stateMatches && districtMatches;
    })
  ), [publications, searchFilters]);

  const publicationYears = useMemo(() => (
    [...new Set(
      publications
        .map((pub) => String(pub?.year || '').trim())
        .filter(Boolean)
    )].sort((a, b) => Number(b) - Number(a))
  ), [publications]);

  const sortedItems = useMemo(() => {
    const sorted = [...filteredPublications];

    if (sortConfig.key) {
      sorted.sort((a, b) => {
        const aVal = normalizeString(a?.[sortConfig.key]);
        const bVal = normalizeString(b?.[sortConfig.key]);

        if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return sorted;
  }, [filteredPublications, sortConfig]);

  const itemsPerPage = 12;
  const totalPages = Math.ceil(sortedItems.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentItems = sortedItems.slice(startIndex, startIndex + itemsPerPage);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchFilters]);

  useEffect(() => {
    if (totalPages === 0 && currentPage !== 1) {
      setCurrentPage(1);
      return;
    }

    if (totalPages > 0 && currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const changePage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const paginationItems = totalPages <= 7
    ? Array.from({ length: totalPages }, (_, index) => index + 1)
    : [
      1,
      ...(currentPage > 3 ? ['ellipsis-start'] : []),
      ...Array.from(
        { length: 3 },
        (_, index) => Math.min(Math.max(currentPage - 1 + index, 2), totalPages - 1)
      ).filter((page, index, pages) => pages.indexOf(page) === index),
      ...(currentPage < totalPages - 2 ? ['ellipsis-end'] : []),
      totalPages
    ];

  const openImg = (item) => {
    setSelectedCard({ data: item });
    setShowModal(true);
  };

  const getModeLabel = (item) => {
    const modeValue = item?.link;
    if (!modeValue && item?.linkUrl) return 'Link';
    if (!modeValue && item?.file) return 'File';

    const matchedMode = lookup.find(
      (entry) => String(entry.Flag) === '7' && String(entry.LookupCode) === String(modeValue)
    );
    return matchedMode?.LookupValue || 'File';
  };

  const resolveAssetUrl = (value, defaultFolder = 'files') => {
    if (!value) return '';
    const raw = String(value).trim();
    if (!raw) return '';

    if (/^https?:\/\//i.test(raw) || /^blob:/i.test(raw) || /^data:/i.test(raw)) return raw;
    if (raw.startsWith('/')) return `${window.location.origin}${raw}`;

    const baseUrl = String(imgURL || '').replace(/\/$/, '');
    if (baseUrl && !raw.startsWith('http')) {
      const cleaned = raw.replace(/^\.\//, '').replace(/^\/+/, '').replace(/^storage\//, '');
      if (/\/publications\/files$/i.test(baseUrl)) {
        return `${baseUrl}/${cleaned.replace(/^publications\/files\//i, '').replace(/^files\//i, '')}`;
      }
      const storagePath = cleaned.startsWith('publications/')
        ? cleaned
        : cleaned.startsWith('thumbnails/')
          ? `publications/${cleaned}`
          : cleaned.startsWith('files/')
            ? `publications/${cleaned}`
            : `publications/${defaultFolder}/${cleaned}`;

      return `${baseUrl}/${storagePath}`;
    }

    return `${window.location.origin}/${raw.replace(/^\.\//, '').replace(/^\/+/, '')}`;
  };

  const getAssetInfo = (item) => {
    const fileValue = item?.file || item?.file_path || item?.media_path || '';
    const rawUrl = item?.linkUrl || item?.link_url || item?.url || '';
    const hasLinkMode = String(getModeLabel(item)).toLowerCase() === 'link';

    if (/^https?:\/\//i.test(String(fileValue).trim())) {
      return { kind: 'link', url: resolveAssetUrl(fileValue), iconClass: 'bi-link-45deg' };
    }

    if (hasLinkMode && rawUrl) {
      return { kind: 'link', url: resolveAssetUrl(rawUrl), iconClass: 'bi-link-45deg' };
    }

    if (!fileValue && rawUrl) {
      return { kind: 'link', url: resolveAssetUrl(rawUrl), iconClass: 'bi-link-45deg' };
    }

    if (!fileValue) {
      return { kind: 'none', url: '', iconClass: 'bi-slash-circle' };
    }

    const normalized = String(fileValue).trim();
    const ext = normalized.split('?')[0].split('#')[0].split('.').pop()?.toLowerCase();

    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'].includes(ext)) {
      return { kind: 'image', url: resolveAssetUrl(normalized, 'files'), iconClass: 'bi-file-earmark-image' };
    }

    if (['mp4', 'webm', 'mov', 'avi', 'mkv', 'mpeg'].includes(ext)) {
      return { kind: 'video', url: resolveAssetUrl(normalized, 'files'), iconClass: 'bi-camera-video' };
    }

    if (['pdf', 'doc', 'docx', 'xls', 'xlsx', 'csv', 'txt', 'zip', 'rar'].includes(ext)) {
      return { kind: 'file', url: resolveAssetUrl(normalized, 'files'), iconClass: 'bi-file-earmark-arrow-down' };
    }

    return { kind: 'file', url: resolveAssetUrl(normalized, 'files'), iconClass: 'bi-file-earmark-arrow-down' };
  };

  const normalizeDownloadUrl = (value) => resolveAssetUrl(value);

  const triggerDownloadAsset = async (url, defaultFileName = 'publication-file') => {
    const safeUrl = normalizeDownloadUrl(url);
    if (!safeUrl) {
      Swal.fire({
        title: 'No file available',
        text: 'This publication does not contain a downloadable file or valid link.',
        icon: 'info',
        confirmButtonText: 'OK'
      });
      return;
    }

    const fileName = (safeUrl.split('/').pop() || defaultFileName).split('?')[0].split('#')[0] || defaultFileName;
    try {
      const response = await fetch(safeUrl, { mode: 'cors' });
      if (!response.ok) throw new Error('Download failed');

      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const blobLink = document.createElement('a');
      blobLink.href = blobUrl;
      blobLink.download = fileName;
      blobLink.style.display = 'none';
      document.body.appendChild(blobLink);
      blobLink.click();
      blobLink.remove();
      setTimeout(() => URL.revokeObjectURL(blobUrl), 2000);
    } catch (error) {
      Swal.fire({
        title: 'Download blocked',
        text: 'The storage server is not allowing this file to be downloaded. Enable CORS for the storage URL or return the file with Content-Disposition: attachment.',
        icon: 'error',
        confirmButtonText: 'OK'
      });
    }
  };

  const downloadPublicationFile = (item) => {
    const asset = getAssetInfo(item);
    if (!asset.url || asset.kind === 'none') {
      Swal.fire({
        title: 'No file available',
        text: 'This publication does not contain a downloadable file or valid link.',
        icon: 'info',
        confirmButtonText: 'OK'
      });
      return;
    }

    const finalUrl = normalizeDownloadUrl(asset.url);

    if (asset.kind === 'link' || !['file', 'image', 'video'].includes(asset.kind)) {
      window.open(finalUrl, '_blank', 'noopener,noreferrer');
      return;
    }

    triggerDownloadAsset(finalUrl, finalUrl.split('/').pop() || 'publication-file');
  };

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleResetFilters = () => {
    setSearchFilters({
      year: '',
      type: '',
      typology: '',
      state: '',
      district: '',
    });
    setDistrictList([]);
    setCurrentPage(1);
  };

  return (
    <>
      <div className="project-search-container">
        {!projectId && <div className="top-image"></div>}
        {!projectId && (
          <div className="container p-0">
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <h2 className="title ms-3">Our Publications</h2>
            </div>
            <div className="search-bar-container">
              <select name="year" value={searchFilters.year} onChange={handleInputChange}>
                <option value="">Select Year</option>
                {publicationYears.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
              <select name="type" value={searchFilters.type} onChange={handleInputChange}>
                <option value="">Select Type</option>
                {contentData
                  ?.sort((a, b) => a.LookupValue.localeCompare(b.LookupValue))
                  .map((type) => (
                    <option key={type.LookupCode} value={type.LookupCode}>
                      {type.LookupValue}
                    </option>
                  ))}
              </select>
              <select name="typology" value={searchFilters.typology} onChange={handleInputChange}>
                <option value="">Select Typology</option>
                {typologyData
                  ?.sort((a, b) => a.LookupValue.localeCompare(b.LookupValue))
                  ?.map((typology) => (String(typology.Flag) === '3' ? (
                    <option key={typology.LookupCode} value={typology.LookupCode}>
                      {typology.LookupValue}
                    </option>
                  ) : null
                  ))}
              </select>
              <select name="state" value={searchFilters.state} onChange={handleInputChange}>
                <option value="">Select State</option>
                {StateData
                  ?.sort((a, b) => a.statename.localeCompare(b.statename))
                  ?.map((state) => (
                    <option key={state?.id_mststate} value={state?.id_mststate}>
                      {state?.statename?.trim()}
                    </option>
                  ))}
              </select>
              <select name="district" value={searchFilters.district} onChange={handleInputChange} disabled={!districtList.length}>
                <option value="">Select District</option>
                {districtList.map((district) => (
                  <option key={district.id_mstdistrict} value={district.id_mstdistrict}>
                    {district.districtname?.trim()}
                  </option>
                ))}
              </select>
              <button
                type="button"
                onClick={handleResetFilters}
                className="btn btn-outline-secondary"
              >
                Reset Filters
              </button>
            </div>
          </div>
        )}

        {projectId && <h2 className="fw-bold mt-2">Publications</h2>}
        <div className="container">
          {!projectId && <div className="row justify-content-end">
            <div className="toggle-container-publication me-4">
              <button
                className={`toggle-option ${!isGridView ? 'active' : ''}`}
                onClick={() => setIsGridView(false)}
              >
                <i className="bi bi-check2-circle me-1"></i>
                <i className="bi bi-list"></i>
              </button>
              <button
                className={`toggle-option ${isGridView ? 'active' : ''}`}
                onClick={() => setIsGridView(true)}
              >
                <i className="bi bi-grid-3x3-gap-fill"></i>
              </button>
            </div>
          </div>}
          {loading ? (
            <div className="text-center py-4">Loading publications...</div>
          ) : !currentItems.length ? (
            <div className="text-center py-4">
              {projectId
                ? 'No publications are available for this project.'
                : 'No publications found for the selected filters.'}
            </div>
          ) : isGridView ? (
            <div className="grid-view publication-gridView">
              {currentItems.map((item, index) => (
                <div className="card publication-card" key={index} style={{ position: 'relative' }}>
                  <button
                    type="button"
                    className="publication-pdf-action"
                    onClick={() => openImg(item)}
                    title="Download PDF"
                    aria-label={`Download PDF: ${item.title}`}
                  >
                    <i className="bi bi-file-earmark-pdf-fill"></i>
                    <i className="bi bi-arrow-down-circle-fill"></i>
                  </button>
                  <h3 className="publication-card-title">{item.title}</h3>
                  <p style={{ fontSize: "small" }}>{item.typologyName}</p>
                  <div className="tags">
                    <span>{item.countryName}</span>
                    <span>{item.stateName}</span>
                    <span>{item.districtName}</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="list-view">
              <table>
                <thead>
                  <tr>
                    <th onClick={() => handleSort('title')} style={{ border: '1px solid #ccc', cursor: 'pointer' }}>Title</th>
                    <th onClick={() => handleSort('typologyName')} style={{ border: '1px solid #ccc', cursor: 'pointer' }}>Typology</th>
                    <th onClick={() => handleSort('typeName')} style={{ border: '1px solid #ccc', cursor: 'pointer' }}>Type</th>
                    <th onClick={() => handleSort('countryName')} style={{ border: '1px solid #ccc', cursor: 'pointer' }}>Country</th>
                    <th onClick={() => handleSort('stateName')} style={{ border: '1px solid #ccc', cursor: 'pointer' }}>State</th>
                    <th onClick={() => handleSort('districtName')} style={{ border: '1px solid #ccc', cursor: 'pointer' }}>District</th>
                    <th onClick={() => handleSort('year')} style={{ border: '1px solid #ccc', cursor: 'pointer' }}>Year</th>
                    <th style={{ border: '1px solid #ccc' }}>Download</th>
                  </tr>
                </thead>
                <tbody>
                  {currentItems.map((proj, index) => (
                    <tr key={index}>
                      <td style={{ border: '1px solid #ccc' }}>{proj.title}</td>
                      <td style={{ border: '1px solid #ccc' }}>{proj.typologyName}</td>
                      <td style={{ border: '1px solid #ccc' }}>{lookup?.find((item) => String(item.Flag) === '4' && String(item.LookupCode) === String(proj.type))?.LookupValue || proj.typeName}</td>
                      <td style={{ border: '1px solid #ccc' }}>{proj.countryName}</td>
                      <td style={{ border: '1px solid #ccc' }}>{proj.stateName}</td>
                      <td style={{ border: '1px solid #ccc' }}>{proj.districtName}</td>
                      <td style={{ border: '1px solid #ccc' }}>{proj.year}</td>
                      <td>
                        <button
                          type="button"
                          className="publication-pdf-action publication-pdf-action-small"
                          onClick={() => openImg(proj)}
                          title="Download PDF"
                          aria-label={`Download PDF: ${proj.title}`}
                        >
                          <i className="bi bi-file-earmark-pdf-fill"></i>
                          <i className="bi bi-arrow-down-circle-fill"></i>
                        </button>
                        {(() => {
                          const asset = getAssetInfo(proj);
                          if (!asset.url || asset.kind === 'none') return null;

                          return (
                            <button
                              type="button"
                              className="publication-list-download-button ms-2"
                              onClick={(e) => {
                                e.stopPropagation();
                                openImg(proj);
                              }}
                              title={asset.kind === 'link' ? 'Open link' : 'Download asset'}
                            >
                              <i className={`${asset.iconClass} text-primary`}></i>
                            </button>
                          );
                        })()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        {!projectId && totalPages > 1 && <div className="publication-pagination">
          <button
            className="publication-pagination-button publication-pagination-arrow"
            onClick={() => changePage(currentPage - 1)}
            disabled={currentPage === 1}
            aria-label="Previous page"
          >
            <i className="bi bi-chevron-left"></i>
          </button>
          {paginationItems.map((item) => item.toString().startsWith('ellipsis') ? (
            <span className="publication-pagination-ellipsis" key={item}>...</span>
          ) : (
            <button
              key={item}
              className={`publication-pagination-button ${currentPage === item ? 'active' : ''}`}
              onClick={() => changePage(item)}
              aria-label={`Page ${item}`}
              aria-current={currentPage === item ? 'page' : undefined}
            >
              {item}
            </button>
          ))}
          <button
            className="publication-pagination-button publication-pagination-arrow"
            onClick={() => changePage(currentPage + 1)}
            disabled={currentPage === totalPages}
            aria-label="Next page"
          >
            <i className="bi bi-chevron-right"></i>
          </button>
        </div>}
        <BannerModal show={showModal} onClose={() => setShowModal(false)} data={selectedCard} />
      </div>
    </>
  )
}

export default Publications;
